<template>
  <div id="stay" class="article-feature" :class="classes">
    <div class="layout">
      <div class="article-feature__images">
        <div class="video">
          <IntersectionObserver v-if="videoUrl" v-slot="{ intersected }">
            <Video
              :intersected="intersected"
              :video="{
                sources: [
                  {
                    url: videoUrl,
                  },
                ],
              }"
            />
          </IntersectionObserver>
        </div>
        <div
          v-for="(image, index) in images"
          :key="index"
          class="image-container"
        >
          <AddFromPhoto
            v-if="image.productHandle"
            :product-handle="image.productHandle"
            :image-url="image.images"
          />
          <Picture v-else :images="image" />
          <div v-if="credit" class="credit">{{ credit }}</div>
        </div>
      </div>
      <div class="copy">
        <div v-if="subHeading" class="article-feature__type">
          {{ subHeading }}
        </div>
        <div v-if="type" class="type">{{ type }}</div>
        <h1 class="heading">{{ heading }}</h1>
        <div class="wysiwyg" v-html="text"></div>
        <Button
          v-if="linkButton"
          tag="a"
          :href="link"
          target="_blank"
          class="linkButton"
        >
          {{ linkButton }}
        </Button>
        <div v-if="productImages" class="embed-image">
          <AddFromPhoto
            v-if="productHandle"
            :product-handle="productHandle"
            :image-url="productImages"
          />
          <Picture v-else :images="productImages" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import Picture from "@/components/Picture.vue";
import AddFromPhoto from "@/components/AddFromPhoto.vue";
import Video from "@/components/Video.vue";
import IntersectionObserver from "@/components/IntersectionObserver.vue";
export default {
  name: "ArticleFeature",
  components: {
    Button,
    Picture,
    AddFromPhoto,
    Video,
    IntersectionObserver,
  },
  props: {
    subHeading: {
      type: String,
      default: null,
    },
    heading: {
      type: String,
      required: true,
    },
    images: {
      type: Array,
      default: null,
    },
    text: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: null,
    },
    credit: {
      type: String,
      default: null,
    },
    productHandle: {
      type: String,
      default: null,
    },
    productHandleTwo: {
      type: String,
      default: null,
    },
    productImages: {
      type: Object,
      default: null,
    },
    videoUrl: {
      type: String,
      default: null,
    },
    block20px: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "orange",
      validator(value) {
        return [
          "orange",
          "brown",
          "blue",
          "green",
          "lightBlue",
          "brazilGreen",
          "maroon",
        ].includes(value);
      },
    },
    linkButton: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
  },
  computed: {
    classes() {
      return {
        "article-feature--orange": this.color === "orange",
        "article-feature--brown": this.color === "brown",
        "article-feature--blue": this.color === "blue",
        "article-feature--green": this.color === "green",
        "article-feature--light": this.color === "light",
        "article-feature--lightBlue": this.color === "lightBlue",
        "article-feature--brazilGreen": this.color === "brazilGreen",
        "article-feature--maroon": this.color === "maroon",
        "article-feature--largeSecondImage": this.largeSecondImage,
        "article-feature--block20px": this.block20px,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.article-feature {
  margin: 0 0 60px 0;
  @include respond-above(md) {
    padding: 0 20px;
    max-width: 1600px;
    margin: 0 auto 80px;
  }
  &__type {
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.55px;
    margin: 0 0 10px;
    text-transform: uppercase;
  }
  &__images {
    grid-area: images;
  }
  &--orange {
    background-color: rgba(214, 116, 18);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }
  &--brown {
    background-color: rgba(221, 210, 183);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-grey-dark;
    }
  }

  &--blue {
    background-color: rgba(77, 117, 168);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }

  &--brazilGreen {
    background-color: #406c25;
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }

  &--green {
    background-color: rgba(58, 88, 77);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }

  &--light {
    background-color: rgba(248, 246, 242);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-grey-dark;
    }
  }

  &--lightBlue {
    background-color: rgba(199, 214, 219);
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }

  &--maroon {
    background-color: #581626;
    .heading,
    .type,
    .wysiwyg :deep() {
      color: $color-white;
    }
  }

  &--largeSecondImage {
    .wysiwyg {
      margin-bottom: 60px;
    }
    .embed-image {
      @include respond-above(md) {
        max-width: 400px;
      }
    }
  }

  &--block20px {
    margin: 0 auto 20px;
  }
}

.layout {
  display: grid;
  grid-template-areas: "copy" "images";
  grid-template-columns: 1fr;
  padding: 30px 20px 20px;
  margin: 0 auto;
  max-width: 1400px;

  @include respond-above(md) {
    column-gap: 40px;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas: "images copy";
    padding: 60px 0;
  }
}

.image-container {
  position: relative;
  &:last-child {
    margin-bottom: 0;
  }

  @include respond-above(md) {
    margin: 0 0 2px;
  }
}

.copy {
  grid-area: copy;
  margin: 0 0 20px 0;
  @include respond-above(md) {
    margin: 0;
    :deep(.tile) {
      display: block;
      max-width: 400px;
    }
  }
}

.heading {
  font-size: 22px;
  font-weight: normal;
  margin: 0 0 20px 0;
  text-transform: uppercase;

  @include respond-above(md) {
    font-size: 40px;
  }
}

.credit {
  bottom: 10px;
  color: $color-white;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.49px;
  left: 10px;
  position: absolute;
}

.type {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.49px;
  margin: 0 0 10px 0;
  text-transform: uppercase;
}

.embed-image {
  @include respond-above(md) {
    max-width: 400px;
  }
}

.wysiwyg :deep() {
  font-family: $heading-font-family;
  font-size: 14px;
  font-weight: 300;
  // margin: 0 0 60px 0;
  margin: 0 0 20px 0;

  @include respond-above(md) {
    font-size: 18px;
  }

  h4 {
    font-size: 16px;
    margin-bottom: 10px;

    @include respond-above(md) {
      font-size: 22px;
    }
  }

  p {
    margin-bottom: 15px;
  }

  *:last-child {
    margin-bottom: 0;
  }
}

.linkButton {
  padding: 0;
  color: $color-grey-light;
  text-decoration: underline;
  border: none;
  background: none;
}
</style>
